/**
 * QuickMixProPanel
 * Slide-out panel for setting personalized Quick Mix preferences.
 * Users set their mixing style once, then every Quick Mix uses it.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, Wand2, Save, RotateCcw, Mic2, Music2, Headphones, Sliders, SunMedium, Gauge } from 'lucide-react';
import { Button } from '../../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PreferenceSlider = ({ label, icon: Icon, value, onChange, leftLabel, rightLabel, testId }) => (
  <div className="space-y-2" data-testid={testId}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-sm text-zinc-300">
        <Icon className="w-4 h-4 text-purple-400" />
        <span>{label}</span>
      </div>
      <span className="text-xs text-zinc-500 font-mono tabular-nums w-8 text-right">
        {value > 0 ? `+${value.toFixed(1)}` : value.toFixed(1)}
      </span>
    </div>
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-zinc-600 w-14 text-right">{leftLabel}</span>
      <input
        type="range"
        min={-2}
        max={2}
        step={0.5}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="flex-1 h-1.5 rounded-full appearance-none bg-zinc-700 accent-purple-500 cursor-pointer
          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 
          [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-400 [&::-webkit-slider-thumb]:shadow-lg
          [&::-webkit-slider-thumb]:shadow-purple-500/30"
      />
      <span className="text-[10px] text-zinc-600 w-14">{rightLabel}</span>
    </div>
  </div>
);

const GENRE_OPTIONS = [
  'Pop', 'Rock', 'Hip-Hop', 'R&B', 'EDM', 'Jazz', 'Classical',
  'Country', 'Metal', 'Lo-Fi', 'Ambient', 'Indie', 'Reggae', 'Latin'
];

export const QuickMixProPanel = ({ isOpen, onClose, userId, onPreferencesSaved }) => {
  const [vocalLevel, setVocalLevel] = useState(0);
  const [bassLevel, setBassLevel] = useState(0);
  const [stereoWidth, setStereoWidth] = useState(0);
  const [brightness, setBrightness] = useState(0);
  const [compression, setCompression] = useState(0);
  const [genre, setGenre] = useState('');
  const [customNotes, setCustomNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Load preferences on open
  useEffect(() => {
    if (!isOpen || loaded) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/ai-mixing/preferences?user_id=${userId || 'default'}`);
        const data = await res.json();
        if (data.preferences) {
          const p = data.preferences;
          setVocalLevel(p.vocal_level ?? 0);
          setBassLevel(p.bass_level ?? 0);
          setStereoWidth(p.stereo_width ?? 0);
          setBrightness(p.brightness ?? 0);
          setCompression(p.compression ?? 0);
          setGenre(p.genre || '');
          setCustomNotes(p.custom_notes || '');
        }
        setLoaded(true);
      } catch {
        setLoaded(true);
      }
    })();
  }, [isOpen, loaded, userId]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const prefs = {
        vocal_level: vocalLevel,
        bass_level: bassLevel,
        stereo_width: stereoWidth,
        brightness,
        compression,
        genre: genre || null,
        custom_notes: customNotes || null,
      };
      await fetch(`${API_URL}/api/ai-mixing/preferences?user_id=${userId || 'default'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(prefs),
      });
      toast.success('Quick Mix Pro preferences saved');
      onPreferencesSaved?.(prefs);
      onClose();
    } catch {
      toast.error('Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  }, [vocalLevel, bassLevel, stereoWidth, brightness, compression, genre, customNotes, userId, onClose, onPreferencesSaved]);

  const handleReset = () => {
    setVocalLevel(0);
    setBassLevel(0);
    setStereoWidth(0);
    setBrightness(0);
    setCompression(0);
    setGenre('');
    setCustomNotes('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="quick-mix-pro-panel">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      {/* Panel */}
      <div className="relative w-full max-w-md bg-zinc-900 border-l border-zinc-700/50 shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-purple-500/15 flex items-center justify-center">
              <Wand2 className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-white">Quick Mix Pro</h2>
              <p className="text-xs text-zinc-500">Your personalized mixing style</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="close-quick-mix-pro">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* Sliders */}
          <div className="space-y-5">
            <PreferenceSlider
              label="Vocals" icon={Mic2} value={vocalLevel} onChange={setVocalLevel}
              leftLabel="Quieter" rightLabel="Louder" testId="pref-vocal-level"
            />
            <PreferenceSlider
              label="Bass" icon={Music2} value={bassLevel} onChange={setBassLevel}
              leftLabel="Lighter" rightLabel="Heavier" testId="pref-bass-level"
            />
            <PreferenceSlider
              label="Stereo Width" icon={Headphones} value={stereoWidth} onChange={setStereoWidth}
              leftLabel="Narrow" rightLabel="Wide" testId="pref-stereo-width"
            />
            <PreferenceSlider
              label="Brightness" icon={SunMedium} value={brightness} onChange={setBrightness}
              leftLabel="Warm" rightLabel="Bright" testId="pref-brightness"
            />
            <PreferenceSlider
              label="Compression" icon={Gauge} value={compression} onChange={setCompression}
              leftLabel="Dynamic" rightLabel="Tight" testId="pref-compression"
            />
          </div>

          {/* Divider */}
          <div className="border-t border-zinc-800" />

          {/* Genre */}
          <div className="space-y-2" data-testid="pref-genre">
            <label className="flex items-center gap-2 text-sm text-zinc-300">
              <Sliders className="w-4 h-4 text-purple-400" />
              Preferred Genre
            </label>
            <div className="flex flex-wrap gap-1.5">
              {GENRE_OPTIONS.map(g => (
                <button
                  key={g}
                  onClick={() => setGenre(genre === g ? '' : g)}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                    genre === g
                      ? 'bg-purple-500/20 border-purple-500/50 text-purple-300'
                      : 'bg-zinc-800/50 border-zinc-700/50 text-zinc-400 hover:border-zinc-600 hover:text-zinc-300'
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Notes */}
          <div className="space-y-2" data-testid="pref-custom-notes">
            <label className="text-sm text-zinc-300">Custom Instructions</label>
            <textarea
              value={customNotes}
              onChange={(e) => setCustomNotes(e.target.value)}
              placeholder="e.g., I always want punchy drums and airy vocals..."
              rows={3}
              maxLength={250}
              className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-purple-500/50 resize-none"
            />
            <div className="text-right text-[10px] text-zinc-600">{customNotes.length}/250</div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-zinc-800 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={handleReset} className="text-zinc-400 hover:text-white" data-testid="reset-preferences">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
            Reset
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isSaving}
            className="bg-purple-600 hover:bg-purple-500 text-white px-4"
            data-testid="save-preferences-btn"
          >
            <Save className="w-3.5 h-3.5 mr-1.5" />
            {isSaving ? 'Saving...' : 'Save Preferences'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuickMixProPanel;
